import React, { useState, useEffect, useCallback } from 'react';
import { stickyNoteAPI, clientAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { StickyNote as StickyNoteIcon, Plus, CheckCircle2, Trash2, RotateCcw, X, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';

/**
 * Sticky Notes widget for dashboards (Staff / Manager / Admin / Super Admin).
 * - Add note (optionally tied to a client).
 * - Filter by client.
 * - Toggle Active / Completed tabs.
 * - Mark Complete moves note to Completed (with timestamp + by whom).
 * - Reopen / Delete available.
 */
export const StickyNotesWidget = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('active');
  const [notes, setNotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [filterClient, setFilterClient] = useState('all');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [newNote, setNewNote] = useState({ client_id: '', content: '' });

  // Sticky-note color palette (rotates per index for visual variety)
  const NOTE_COLORS = [
    { bg: 'bg-yellow-50',   border: 'border-yellow-300',   ribbon: 'bg-yellow-400' },
    { bg: 'bg-pink-50',     border: 'border-pink-300',     ribbon: 'bg-pink-400' },
    { bg: 'bg-blue-50',     border: 'border-blue-300',     ribbon: 'bg-blue-400' },
    { bg: 'bg-emerald-50',  border: 'border-emerald-300',  ribbon: 'bg-emerald-400' },
    { bg: 'bg-violet-50',   border: 'border-violet-300',   ribbon: 'bg-violet-400' },
    { bg: 'bg-orange-50',   border: 'border-orange-300',   ribbon: 'bg-orange-400' },
  ];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: tab };
      if (filterClient && filterClient !== 'all') params.client_id = filterClient;
      const res = await stickyNoteAPI.getAll(params);
      setNotes(res.data || []);
    } catch (e) {
      // Silent fail — dashboard should still render
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [tab, filterClient]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    clientAPI.getAll()
      .then(res => setClients(res.data || []))
      .catch(() => setClients([]));
  }, []);

  const handleCreate = async () => {
    if (!newNote.content.trim()) {
      toast.warning('Write something first');
      return;
    }
    try {
      await stickyNoteAPI.create({
        client_id: newNote.client_id || null,
        content: newNote.content.trim(),
      });
      toast.success('Note added');
      setNewNote({ client_id: '', content: '' });
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add note');
    }
  };

  const handleComplete = async (id) => {
    try {
      await stickyNoteAPI.complete(id);
      toast.success('Marked complete');
      load();
    } catch (e) {
      toast.error('Failed to mark complete');
    }
  };

  const handleReopen = async (id) => {
    try {
      await stickyNoteAPI.reopen(id);
      toast.success('Note reopened');
      load();
    } catch (e) {
      toast.error('Failed to reopen');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this note permanently?')) return;
    try {
      await stickyNoteAPI.delete(id);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <Card className="border-0 shadow-md bg-gradient-to-br from-amber-50/40 to-white" data-testid="sticky-notes-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <StickyNoteIcon className="w-5 h-5 text-amber-600" />
            Sticky Notes
            <span className="text-xs font-normal text-muted-foreground">
              ({notes.length})
            </span>
          </CardTitle>
          <div className="flex items-center gap-2">
            {/* Tabs */}
            <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs">
              <button
                type="button"
                onClick={() => setTab('active')}
                className={`px-3 py-1.5 font-medium transition-colors ${tab === 'active' ? 'bg-amber-100 text-amber-900' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                data-testid="notes-tab-active"
              >
                Active
              </button>
              <button
                type="button"
                onClick={() => setTab('completed')}
                className={`px-3 py-1.5 font-medium transition-colors border-l border-slate-200 ${tab === 'completed' ? 'bg-emerald-100 text-emerald-900' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                data-testid="notes-tab-completed"
              >
                Completed
              </button>
            </div>
            {!showForm && tab === 'active' && (
              <Button
                size="sm"
                onClick={() => setShowForm(true)}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="add-sticky-note-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add
              </Button>
            )}
          </div>
        </div>

        {/* Client filter */}
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Filter:</span>
          <Select value={filterClient} onValueChange={setFilterClient}>
            <SelectTrigger className="h-8 text-xs w-56" data-testid="notes-client-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients (incl. general)</SelectItem>
              {clients.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.organization_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Add form */}
        {showForm && tab === 'active' && (
          <div className="rounded-lg border-2 border-amber-300 bg-amber-50/60 p-3 space-y-2" data-testid="new-note-form">
            <Select
              value={newNote.client_id}
              onValueChange={v => setNewNote(n => ({ ...n, client_id: v === '__none__' ? '' : v }))}
            >
              <SelectTrigger className="h-9 text-sm" data-testid="new-note-client">
                <SelectValue placeholder="Client (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">General (no client)</SelectItem>
                {clients.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.organization_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea
              rows={3}
              placeholder="Write your note here..."
              value={newNote.content}
              onChange={e => setNewNote(n => ({ ...n, content: e.target.value }))}
              data-testid="new-note-content"
            />
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setShowForm(false); setNewNote({ client_id: '', content: '' }); }}
              >
                <X className="w-3.5 h-3.5 mr-1" /> Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleCreate}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="save-new-note-btn"
              >
                Save Note
              </Button>
            </div>
          </div>
        )}

        {/* Notes list */}
        {loading ? (
          <p className="text-sm text-muted-foreground text-center py-6">Loading...</p>
        ) : notes.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">
              {tab === 'active' ? 'No active notes. Add one to get started!' : 'No completed notes yet.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {notes.map((n, idx) => {
              const color = NOTE_COLORS[idx % NOTE_COLORS.length];
              const isCompleted = n.status === 'completed';
              return (
                <div
                  key={n.id}
                  className={`relative flex items-start gap-3 rounded-lg border ${color.border} ${color.bg} pl-3 pr-2 py-2 hover:shadow-sm transition-all`}
                  data-testid={`sticky-note-${n.id}`}
                >
                  {/* Color ribbon */}
                  <span className={`absolute left-0 top-2 bottom-2 w-1 rounded-r ${color.ribbon}`}></span>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      {n.client_name ? (
                        <Badge variant="outline" className="text-[10px] font-semibold">{n.client_name}</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] text-slate-500">General</Badge>
                      )}
                      {isCompleted && (
                        <Badge className="bg-emerald-500 text-white text-[10px]">
                          <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> Done
                        </Badge>
                      )}
                      <span className="text-[10px] text-slate-500 ml-auto">
                        {formatDateTime(n.created_at)}
                      </span>
                    </div>
                    <p className={`text-sm whitespace-pre-wrap break-words ${isCompleted ? 'line-through text-slate-500' : 'text-slate-800'}`}>
                      {n.content}
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      By <strong>{n.created_by_name || '—'}</strong>
                      {isCompleted && n.completed_at && (
                        <span className="text-emerald-700">
                          {' · '}Completed by <strong>{n.completed_by_name || '—'}</strong> on {formatDateTime(n.completed_at)}
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!isCompleted && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[11px] px-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        onClick={() => handleComplete(n.id)}
                        data-testid={`note-complete-${n.id}`}
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Done
                      </Button>
                    )}
                    {isCompleted && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[11px] px-2 border-slate-300 text-slate-600 hover:bg-slate-50"
                        onClick={() => handleReopen(n.id)}
                        data-testid={`note-reopen-${n.id}`}
                      >
                        <RotateCcw className="w-3 h-3 mr-1" /> Reopen
                      </Button>
                    )}
                    {(n.created_by === user?.id || ['super_admin','admin','manager'].includes(user?.role)) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-red-500 hover:bg-red-50"
                        onClick={() => handleDelete(n.id)}
                        data-testid={`note-delete-${n.id}`}
                        title="Delete note"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StickyNotesWidget;
